/**
 * Cross-app channel for the "single right-hand sider" policy.
 *
 * Only one right-hand sider may be visible at a time across the shell and its
 * micro-frontends. The shell hosts the EventComposer / EventDetails sider
 * (driven by the canonical `event` query param — see `eventParam.ts`); MFEs
 * (office, schedule, …) host their own right-hand siders.
 *
 * Two directions:
 *
 *  1. Composer opens over an already-open MFE sider. The MFE simply hides its
 *     own sider while `event` is present and restores it when the param
 *     clears. That is pure local derivation from the URL — it needs NO event
 *     on this channel.
 *
 *  2. An MFE sider wants to open while the composer is already open. The MFE
 *     emits `request-foreground`; the shell asks its composer to close,
 *     running the composer's unsaved-changes guard (the discard dialog). If
 *     the composer closes, the `event` param clears and the MFE proceeds. If
 *     the user backs out of the discard dialog the composer stays open and the
 *     shell emits `foreground-denied`, so the MFE drops its pending open
 *     rather than leaving it queued for the next time the composer happens to
 *     close.
 *
 * ******* DO NOT MAKE BACKWARDS INCOMPATIBLE CHANGES *******
 * Shell and apps deploy independently. Renaming a channel constant or removing
 * a payload field can silently break listeners or emitters that have not yet
 * been redeployed.
 *
 * Safe changes:
 *   - Add a new optional field to a detail type.
 *   - Add a new well-known `SiderId` value (ids are free-form strings).
 *
 * Unsafe changes:
 *   - Rename or remove a channel constant.
 *   - Remove or rename a field on a detail type.
 *   - Change a field's type or semantics.
 *
 * If an incompatible change is unavoidable, introduce a new constant
 * (`dashboard:sider:request-foreground:v2`) and run both for one release.
 */
/** Emitted by an MFE sider asking to take the right-hand slot. */
export var SIDER_REQUEST_FOREGROUND_CHANNEL = 'dashboard:sider:request-foreground';
/**
 * Emitted by the current slot holder (the shell composer) when it declined to
 * yield — e.g. the user backed out of the unsaved-changes discard dialog.
 */
export var SIDER_FOREGROUND_DENIED_CHANNEL = 'dashboard:sider:foreground-denied';
/**
 * Emitted by an MFE to report whether its own right-hand sider currently
 * occupies the slot (i.e. it has sider content it would show, independent of
 * whether the shell composer is momentarily covering it).
 *
 * The shell uses this purely for a presentation choice: when it opens the
 * composer over an *occupied* slot (office handing off from its space/desk
 * details sider), it swaps instantly so the two equal-width siders cancel and
 * the map doesn't wobble; when the slot is *empty* (schedule's meeting-rooms
 * grid, no competing sider), it slides the composer in. It never gates
 * behavior — a missing or stale signal only affects whether the open animates.
 *
 * Report content-level occupancy, NOT visibility: keep it `true` while the
 * composer covers your sider (you still "own" the slot), so the shell reads a
 * stable value across the handoff rather than racing your suspend.
 */
export var SIDER_OCCUPANCY_CHANNEL = 'dashboard:sider:occupancy';
/** The shell-hosted EventComposer / EventDetails sider. */
export var SHELL_COMPOSER_SIDER_ID = 'shell-composer';
/** The shell-hosted Robin Assistant (agent chat) sider. */
export var SHELL_AGENT_SIDER_ID = 'shell-agent-chat';
